import * as Constants from '../../../constants';
import { Cross } from '../../atoms';
import { FileInProgressModal } from '../../molecules';
import { FileUpload } from '@u1/aligntech-uikit';
import { bulkUploadCanaryUsersRequest } from '../../../clients/canaryUsers';
import { useGlobalContext } from '../../context';
import ChosenFileModal from '../../molecules/ChosenFileModal/ChosenFileModal';
import FileErrorModal from '../../molecules/FileErrorModal/FileErrorModal';
import FileUploadResultModal from '../../molecules/FileResultModal/FileUploadResultModal';
import React from 'react';
import useStyles from './UsersFileUpload.styles';

interface IUsersFileUploadProps {
  open?: boolean;
  uploadBtnText?: string;
  acceptMultiple?: boolean;
  closeFileIsUploadedModal: () => void;
  handleGeneralError: (text: string, title: string) => void;
}

interface IUsersBulkUploadModal {
  isShowFileUploadResultsModal: boolean;
  addedUsers?: number;
  notFoundUsers?: number;
  duplicatedUsers?: number;
  usersNotFound?: string[];
  isShowSuccessMessageOnly: boolean;
}

export const UsersFileUpload: React.FC<IUsersFileUploadProps> = ({
  open = false,
  uploadBtnText,
  acceptMultiple = false,
  closeFileIsUploadedModal,
  handleGeneralError,
}) => {
  const classes = useStyles();
  const { appConfig } = useGlobalContext();
  const [isShowModalUploadFile, setIsShowModalUploadFile] =
    React.useState<boolean>();
  const [isShowModalFileUploadInProgress, setShowModalFileUploadInProgress] =
    React.useState<boolean>();
  const [isShowFileUploadMainModal, setShowFileUploadMainModal] =
    React.useState<boolean>();
  const [fileUploadResultsModal, setFileUploadResultsModal] =
    React.useState<IUsersBulkUploadModal>({
      isShowFileUploadResultsModal: false,
      addedUsers: 0,
      notFoundUsers: 0,
      duplicatedUsers: 0,
      usersNotFound: [] as string[],
      isShowSuccessMessageOnly: false,
    });
  const [fileToUpload, setFileToUpload] = React.useState<File | null>();

  const [fileUploadErrorModal, setFileUploadErrorModal] = React.useState({
    isShowFileUploadErrorModal: false,
    title: '',
  });

  const setFileUploadModalErrorText = (title = '') => {
    if (title) {
      setFileUploadErrorModal({
        isShowFileUploadErrorModal: true,
        title: title,
      });
    } else {
      setFileUploadErrorModal({
        isShowFileUploadErrorModal: false,
        title: title,
      });
    }
  };

  const onFileAddedHandler = (fileList: FileList) => {
    setIsShowModalUploadFile(true);
    setFileToUpload(fileList.item(0));
  };

  const handleFileUploadErrorModal = () => {
    setFileUploadModalErrorText();
    setShowFileUploadMainModal(false);
  };

  const handleFileUploadResultIsShownModal = () => {
    setFileUploadResultsModal({
      isShowFileUploadResultsModal: false,
      addedUsers: 0,
      notFoundUsers: 0,
      duplicatedUsers: 0,
      usersNotFound: [],
      isShowSuccessMessageOnly: false,
    });
    closeFileIsUploadedModal();
  };

  const onFileUploadHandler = () => {
    setIsShowModalUploadFile(false);
    setShowFileUploadMainModal(false);
    setShowModalFileUploadInProgress(true);
    return bulkUploadCanaryUsersRequest(appConfig, fileToUpload)
      .then(response => {
        setShowModalFileUploadInProgress(false);
        setFileUploadResultsModal({
          isShowFileUploadResultsModal: true,
          addedUsers: response.added,
          notFoundUsers: response.notFound,
          duplicatedUsers: response.duplicated,
          usersNotFound: response.usersNotFound,
          isShowSuccessMessageOnly: !response,
        });
      })
      .catch(async reason => {
        setShowModalFileUploadInProgress(false);
        if (reason.toString().includes('SyntaxError')) {
          setFileUploadResultsModal({
            isShowFileUploadResultsModal: true,
            isShowSuccessMessageOnly: true,
          });
        } else {
          if (reason.status && reason.status == 409) {
            const jsonReason = await reason.text();
            setFileUploadModalErrorText(JSON.parse(jsonReason).message);
          } else {
            handleGeneralError(
              Constants.GENERAL_ERROR_MESSAGE,
              Constants.GENERAL_ERROR_TITLE,
            );
          }
        }
      });
  };

  const onFileUploadCloseHandler = () => {
    closeFileIsUploadedModal();
  };

  const onUploadChosenFileModalCloseHandler = () => {
    setIsShowModalUploadFile(false);
    closeFileIsUploadedModal();
  };

  return (
    <>
      {open && (
        <div className={classes.root}>
          {!isShowFileUploadMainModal && (
            <>
              <div className={classes.closeButton}>
                <Cross onClick={onFileUploadCloseHandler} />
              </div>
              <div className={classes.fileUpload} data-qa-name="input_file">
                <FileUpload
                  onUpload={onFileAddedHandler}
                  uploadBtnText={uploadBtnText}
                  acceptMultiple={acceptMultiple}
                />
              </div>
            </>
          )}

          {/* ------------------------- */}
          {/* CONFIRM FILE UPLOAD MODAL */}
          {/* ------------------------- */}
          <ChosenFileModal
            open={isShowModalUploadFile}
            text={fileToUpload?.name}
            buttonText="Upload"
            buttonSize="small"
            handleButtonClick={onFileUploadHandler}
            handleClose={onUploadChosenFileModalCloseHandler}
            iconType="upload"
          />

          {/* ----------------------------- */}
          {/* FILE UPLOAD IN PROGRESS MODAL */}
          {/* ----------------------------- */}
          <FileInProgressModal open={isShowModalFileUploadInProgress} />

          {/* ----------------------- */}
          {/* FILE UPLOAD ERROR MODAL */}
          {/* ----------------------- */}
          <FileErrorModal
            open={fileUploadErrorModal.isShowFileUploadErrorModal}
            headerText={fileUploadErrorModal.title}
            text={Constants.CHOOSE_ANOTHER_FILE_MESSAGE}
            buttonText="Ok"
            buttonSize="small"
            handleButtonClick={handleFileUploadErrorModal}
          />

          {/* ------------------------ */}
          {/* FILE UPLOAD RESULT MODAL */}
          {/* ------------------------ */}
          <FileUploadResultModal
            open={fileUploadResultsModal.isShowFileUploadResultsModal}
            headerText={Constants.ADD_A_FILE_TITLE}
            text={Constants.ADD_A_FILE_SUCCESS_MESSAGE}
            addedUsers={fileUploadResultsModal.addedUsers}
            notFoundUsers={fileUploadResultsModal.notFoundUsers}
            duplicatedUsers={fileUploadResultsModal.duplicatedUsers}
            usersNotFoundList={fileUploadResultsModal.usersNotFound}
            downloadMessageText={Constants.DOWNLOAD_NOT_FOUND_USERS_MESSAGE}
            showSuccessMessageOnly={
              fileUploadResultsModal.isShowSuccessMessageOnly
            }
            buttonText="Ok"
            buttonSize="small"
            handleButtonClick={handleFileUploadResultIsShownModal}
          />
        </div>
      )}
    </>
  );
};
